/**
 * @module useDragDropList
 * @description Reusable composable for drag-and-drop list reordering.
 * Provides state and handlers for enabling drag-and-drop reordering of any array.
 *
 * @example
 * ```typescript
 * const items = ref([{ id: 1, name: 'A' }, { id: 2, name: 'B' }]);
 * const {
 *   draggedIndex,
 *   dropTargetIndex,
 *   onDragStart,
 *   onDragEnter,
 *   onDragEnd,
 *   onDrop,
 *   moveItem
 * } = useDragDropList(items);
 * ```
 *
 * @template T - The type of items in the list
 * @param items - Ref to the array of items to enable drag-drop on
 * @param options - Optional configuration
 *   - onReorder: Callback after items are reordered
 *   - sequenceKey: Property key to update with new sequence (e.g., 'sequence')
 *
 * @used_by
 * - ResultTemplateBuilder.vue (column reordering)
 * - BroadsheetTemplateBuilder.vue (column reordering)
 */

export interface DragDropOptions {
    /** Callback after items are reordered */
    onReorder?: () => void;
    /** Property key to update with new sequence number */
    sequenceKey?: string;
}

export function useDragDropList<T>(items: Ref<T[]>, options: DragDropOptions = {}) {
  const draggedIndex = ref<number | null>(null);
  const dropTargetIndex = ref<number | null>(null);

  /**
     * Handle drag start event
     */
  function onDragStart(index: number, event: DragEvent) {
    draggedIndex.value = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.dropEffect = 'move';
    }
  }

  /**
     * Handle drag enter event (when dragging over a potential drop target)
     */
  function onDragEnter(index: number) {
    if (draggedIndex.value !== index) {
      dropTargetIndex.value = index;
    }
  }

  /**
     * Handle drag end event (cleanup)
     */
  function onDragEnd() {
    draggedIndex.value = null;
    dropTargetIndex.value = null;
  }

  /**
     * Handle drop event - reorder items in the list
     */
  function onDrop(_event?: DragEvent) {
    if (draggedIndex.value === null || dropTargetIndex.value === null || draggedIndex.value === dropTargetIndex.value) {
      onDragEnd();
      return;
    }

    const itemToMove = items.value[draggedIndex.value];
    items.value.splice(draggedIndex.value, 1);
    items.value.splice(dropTargetIndex.value, 0, itemToMove);

    // Update sequence numbers if configured
    if (options.sequenceKey) {
      updateSequence();
    }

    onDragEnd();
    options.onReorder?.();
  }

  /**
     * Move item up or down by direction (-1 for up, +1 for down)
     */
  function moveItem(index: number, direction: -1 | 1) {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.value.length) return;

    const temp = items.value[index];
    items.value[index] = items.value[newIndex];
    items.value[newIndex] = temp;

    if (options.sequenceKey) {
      updateSequence();
    }
    options.onReorder?.();
  }

  /**
     * Remove item at index
     */
  function removeItem(index: number) {
    items.value.splice(index, 1);
    if (options.sequenceKey) {
      updateSequence();
    }
    options.onReorder?.();
  }

  /**
     * Update sequence property on all items based on current order
     */
  function updateSequence() {
    if (!options.sequenceKey) return;
    items.value.forEach((item, i) => {
      (item as Record<string, unknown>)[options.sequenceKey!] = i;
    });
  }

  return {
    // State
    draggedIndex,
    dropTargetIndex,

    // Handlers (bind to template events)
    onDragStart,
    onDragEnter,
    onDragEnd,
    onDrop,

    // Utility methods
    moveItem,
    removeItem,
    updateSequence,
  };
}
